import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type MenuLayout = 'left' | 'top';
export type MenuAlign = 'left' | 'center';

/**
 * Posizione del menu del portale per l'azienda corrente: 'left' (sidebar) | 'top' (barra orizzontale).
 * Con menu in alto il valore persistito può essere 'top-center' (voci centrate nell'header):
 * la posizione resta 'top', l'allineamento viaggia nell'attributo html[kcs-menu-align].
 * Il valore persistito arriva dal backend (cmp_menu_layout, tab Impostazioni anagrafica) via AuthGuard;
 * qui viene cacheato in localStorage per applicarlo subito al load successivo senza flash di layout.
 * L'applicazione avviene via attributo html[kcs-menu-layout] (stesso pattern di kcs-theme).
 */
@Injectable({ providedIn: 'root' })
export class MenuLayoutService {
	private layoutSubject = new BehaviorSubject<MenuLayout>(MenuLayoutService.parseLayout(MenuLayoutService.readStored()));
	layout$ = this.layoutSubject.asObservable();

	constructor() {
		this.setAttributes(MenuLayoutService.readStored());
	}

	get layout(): MenuLayout {
		return this.layoutSubject.value;
	}

	apply(layout: string | null | undefined): void {
		const raw = layout === 'top' || layout === 'top-center' ? layout : 'left';
		localStorage.setItem('menu_layout', raw);
		this.setAttributes(raw);
		const value = MenuLayoutService.parseLayout(raw);
		if (value !== this.layoutSubject.value) {
			this.layoutSubject.next(value);
		}
	}

	/**
	 * Riporta il layout al default 'left' (senza persistere): da chiamare al logout,
	 * così l'azienda successiva non eredita l'attributo html[kcs-menu-layout] della
	 * precedente prima che l'AuthGuard rifetchi il valore corretto.
	 */
	reset(): void {
		this.setAttributes('left');
		if (this.layoutSubject.value !== 'left') {
			this.layoutSubject.next('left');
		}
	}

	private setAttributes(raw: string): void {
		document.documentElement.setAttribute('kcs-menu-layout', MenuLayoutService.parseLayout(raw));
		document.documentElement.setAttribute('kcs-menu-align', MenuLayoutService.parseAlign(raw));
	}

	private static parseLayout(raw: string): MenuLayout {
		return raw === 'top' || raw === 'top-center' ? 'top' : 'left';
	}

	private static parseAlign(raw: string): MenuAlign {
		return raw === 'top-center' ? 'center' : 'left';
	}

	private static readStored(): string {
		const raw = localStorage.getItem('menu_layout');
		return raw === 'top' || raw === 'top-center' ? raw : 'left';
	}
}
